import { useEffect, useRef, useState } from 'react';

/** Frame count of the ScreenLoader Lottie mark (SekretlyLoading3_*.json). */
export const SCREEN_LOADER_TOTAL_FRAMES = 21;
const FRAME_RATE = 30;

/** One full draw-in + hold cycle of the ScreenLoader mark, in ms. */
export const SCREEN_LOADER_DURATION_MS = (SCREEN_LOADER_TOTAL_FRAMES / FRAME_RATE) * 1000;

/**
 * One full hold → erase → redraw cycle of the cold-launch orange loader
 * (loading-launch-orange.mp4, rendered from Sekretly Loading Logo.aep), in
 * ms — 72 frames at 30fps. Used instead of SCREEN_LOADER_DURATION_MS
 * specifically for the once-per-session cold-launch loader in App.tsx, so
 * it never gets cut off mid-cycle even if auth resolves faster than 2.4s.
 */
export const COLD_LAUNCH_LOADER_DURATION_MS = (72 / FRAME_RATE) * 1000;

function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * useMinLoadDuration — holds a "still loading" boolean true for at least
 * `minDurationMs` after it first becomes true, even if `isLoading` flips
 * back to false sooner. Use this to gate a ScreenLoader so its full
 * draw-in-and-hold cycle always plays instead of flashing on fast loads.
 *
 * Skips the enforced minimum when the user prefers reduced motion.
 */
export function useMinLoadDuration(isLoading: boolean, minDurationMs: number = SCREEN_LOADER_DURATION_MS): boolean {
  const [show, setShow] = useState(isLoading);
  const startRef = useRef<number | null>(isLoading ? Date.now() : null);

  useEffect(() => {
    if (isLoading) {
      if (startRef.current === null) startRef.current = Date.now();
      setShow(true);
      return;
    }
    if (startRef.current === null) {
      setShow(false);
      return;
    }
    const effectiveMin = prefersReducedMotion() ? 0 : minDurationMs;
    const remaining = effectiveMin - (Date.now() - startRef.current);
    if (remaining <= 0) {
      startRef.current = null;
      setShow(false);
      return;
    }
    const timer = setTimeout(() => {
      startRef.current = null;
      setShow(false);
    }, remaining);
    return () => clearTimeout(timer);
  }, [isLoading, minDurationMs]);

  return show;
}

let sessionHasShownFirstLoad = false;

export type LoadIndicatorVariant = 'screen' | 'ring' | null;

/**
 * useLoadIndicator — decides which loading indicator a page-level content
 * gate should show. The first content load of the browser session (right
 * after the auth gate resolves) plays the full ScreenLoader animation, held
 * for its minimum duration, so the branded mark and the page's own data
 * fetch read as one continuous load. Every load after that — switching
 * sidebar tabs, revisiting a page, or a later refetch on a page that's
 * already shown content once — uses the plain ring instead, with no forced
 * wait, since the app shell is already on screen.
 *
 * Resets on a hard refresh (it's a module-level flag, not persisted).
 *
 * The 'screen' variant can only ever be returned by the one gate that wins
 * `eligibleForScreen` for the whole session — i.e. every current caller of
 * this hook (all in App.tsx) is, by construction, the cold-launch loader.
 * That's why the minimum hold below matches the cold-launch orange video's
 * own duration rather than the generic ScreenLoader one.
 */
export function useLoadIndicator(isLoading: boolean): LoadIndicatorVariant {
  const [eligibleForScreen] = useState(() => !sessionHasShownFirstLoad);
  const consumedRef = useRef(false);
  const showScreen = useMinLoadDuration(eligibleForScreen && !consumedRef.current && isLoading, COLD_LAUNCH_LOADER_DURATION_MS);

  useEffect(() => {
    if (eligibleForScreen && !consumedRef.current && !showScreen) {
      consumedRef.current = true;
      sessionHasShownFirstLoad = true;
    }
  }, [eligibleForScreen, showScreen]);

  if (eligibleForScreen && !consumedRef.current) return showScreen ? 'screen' : null;
  return isLoading ? 'ring' : null;
}
